// These MUI icons are temporary while the settings sidebar is being redesigned
import {
  AutoAwesomeOutlined as AgentLibraryIcon,
  Settings as AdminSettingsIcon,
  Receipt as BudgetApprovalsIcon,
  Flag as FeatureFlagsIcon,
  SyncAlt as EnvironmentSyncIcon,
  Translate,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { SidebarSections } from '@omnea-app/fe-app-shell/sidebar/SidebarSections'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useShowDocumentReview } from '@omnea-app/fe-org-settings/useShowDocumentReview'
import { useVisibleCatalogueSettings } from '@omnea-app/fe-settings/catalogues/useVisibleCatalogueSettings'
import { FeatureFlagsEnum } from '@omnea-app/packages/universal/feature-flags/featureFlags'
import { useIntegrationsItems } from '../hooks/useIntegrationsItems'
import { getSettingsIconByName } from './SettingsIcon'
import type { SidebarSection } from '@omnea-app/fe-app-shell/sidebar/sectionItem'

export const SettingsSidebarContent = () => {
  const { t: tCommon } = useTranslation('common')
  const { t: tSettings } = useTranslation('settings')
  const featureFlags = useFeatureFlags()
  const showDocumentReview = useShowDocumentReview()
  const routeAccess = useRouteAccess()
  const integrationsItems = useIntegrationsItems(routeAccess.get('settingsIntegrations') === 'allowed')
  const hasVisibleCatalogueSettings = useVisibleCatalogueSettings().length > 0

  const personalSettingSection: SidebarSection = {
    title: tCommon('sidebar.account-title'),
    items: [
      {
        label: tCommon('sidebar.notifications'),
        icon: getSettingsIconByName('notifications'),
        destination: { kind: 'route', routeKey: 'settingsNotifications' },
      },
      {
        label: tCommon('sidebar.preferences'),
        icon: getSettingsIconByName('preferences'),
        destination: { kind: 'route', routeKey: 'settingsPreferences' },
      },
    ],
  }

  const organisationSection: SidebarSection = {
    title: tCommon('sidebar.organisation-title'),
    items: [
      {
        label: tCommon('sidebar.default-notifications'),
        icon: getSettingsIconByName('notifications'),
        destination: { kind: 'route', routeKey: 'settingsDefaultNotifications' },
      },
      { label: tCommon('sidebar.ai'), icon: getSettingsIconByName('ai'), destination: { kind: 'route', routeKey: 'settingsAi' } },
      {
        label: tCommon('sidebar.security'),
        icon: getSettingsIconByName('security'),
        destination: { kind: 'route', routeKey: 'settingsSecurity' },
      },
      {
        label: tCommon('sidebar.core-data'),
        icon: getSettingsIconByName('core-data'),
        destination: { kind: 'route', routeKey: 'settingsCoreData' },
      },
      {
        label: tCommon('sidebar.users-and-groups'),
        icon: getSettingsIconByName('users'),
        destination: { kind: 'route', routeKey: 'settingsUsersAndGroups' },
      },
      {
        label: tCommon('sidebar.permissions'),
        icon: getSettingsIconByName('security'),
        destination: { kind: 'route', routeKey: 'settingsPermissions' },
      },
      {
        label: tCommon('sidebar.user-properties'),
        icon: getSettingsIconByName('user-properties'),
        destination: { kind: 'route', routeKey: 'settingsUserProperties' },
      },
      {
        label: tCommon('sidebar.api'),
        icon: getSettingsIconByName('api'),
        destination: { kind: 'route', routeKey: 'settingsApi' },
      },
      {
        label: tCommon('sidebar.import'),
        icon: getSettingsIconByName('import'),
        destination: { kind: 'route', routeKey: 'settingsImport' },
      },
      {
        label: tCommon('sidebar.audit-log'),
        icon: getSettingsIconByName('audit-log'),
        destination: { kind: 'route', routeKey: 'settingsAuditLog' },
      },
      {
        label: tCommon('sidebar.external-archive'),
        icon: getSettingsIconByName('audit-log'),
        destination: { kind: 'route', routeKey: 'settingsExternalArchive' },
        showsOmneanOnlyBadge: true,
      },
    ],
  }

  const featuresSection: SidebarSection = {
    title: tCommon('sidebar.features-title'),
    items: [
      {
        label: tCommon('sidebar.agent-library'),
        icon: <AgentLibraryIcon sx={{ width: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsAgentLibrary' },
      },
      {
        label: tCommon('sidebar.workflows'),
        icon: getSettingsIconByName('workflows'),
        destination: { kind: 'route', routeKey: 'settingsWorkflows' },
      },
      {
        label: tCommon('sidebar.budget-approvals'),
        icon: <BudgetApprovalsIcon sx={{ width: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsDynamicAssignees' },
      },
      {
        label: tCommon('sidebar.forms'),
        icon: getSettingsIconByName('questions'),
        destination: { kind: 'route', routeKey: 'settingsForms' },
      },
      {
        label: tCommon('sidebar.sourcing'),
        icon: getSettingsIconByName('sourcing'),
        destination: { kind: 'route', routeKey: 'settingsSourcing' },
      },
      {
        label: tCommon('sidebar.auctions'),
        icon: getSettingsIconByName('auctions'),
        destination: { kind: 'route', routeKey: 'settingsAuctions' },
      },
      {
        label: tCommon('sidebar.risk-management'),
        icon: getSettingsIconByName('risk-management'),
        destination: { kind: 'route', routeKey: 'settingsRisks' },
      },
      // Which catalogue kinds an organisation has is data, not a route rule, so it stays an item condition.
      ...(hasVisibleCatalogueSettings
        ? [
            {
              label: tCommon('sidebar.catalogues'),
              icon: getSettingsIconByName('catalogues'),
              destination: { kind: 'route', routeKey: 'settingsCatalogues' } as const,
            },
          ]
        : []),
      {
        label: tCommon('sidebar.product-supplier-categories'),
        icon: getSettingsIconByName('product-supplier-categories'),
        destination: { kind: 'route', routeKey: 'settingsCategories' },
      },
      {
        label: showDocumentReview ? tCommon('sidebar.document-review-playbooks') : tCommon('sidebar.document-storage'),
        icon: getSettingsIconByName('document-storage'),
        destination: { kind: 'route', routeKey: 'settingsDocumentStorage' },
      },
      {
        label: tCommon('sidebar.policies'),
        icon: getSettingsIconByName('policies'),
        destination: { kind: 'route', routeKey: 'settingsPolicies' },
      },
      {
        label: tCommon('sidebar.environment-sync'),
        icon: <EnvironmentSyncIcon sx={{ width: 18, height: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsEnvironmentSync' },
      },
      {
        label: tCommon('sidebar.feature-flags', 'Feature flags'),
        icon: <FeatureFlagsIcon sx={{ width: 18, height: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsInternalFeatureFlags' },
        showsOmneanOnlyBadge: true,
      },
      {
        label: tCommon('sidebar.localisation'),
        icon: <Translate sx={{ width: 18, height: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsLocalisation' },
      },
      {
        label: tCommon('sidebar.admin-settings'),
        icon: <AdminSettingsIcon sx={{ width: 18, height: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsAdmin' },
      },
      // The migration tool exists to migrate organisations onto agreements, so the flag hides the item
      // for those already on them rather than gating the route.
      ...(featureFlags[FeatureFlagsEnum.CREATE_AGREEMENT] === true
        ? [
            {
              label: tCommon('sidebar.agreements-migration'),
              icon: <AdminSettingsIcon sx={{ width: 18, height: 18 }} />,
              destination: { kind: 'route', routeKey: 'settingsAgreementsMigration' } as const,
              showsOmneanOnlyBadge: true,
            },
          ]
        : []),
    ],
  }

  const accountsPayableSection: SidebarSection = {
    title: tCommon('sidebar.accounts-payable-title'),
    items: [
      {
        label: tCommon('sidebar.goods-receipting'),
        icon: getSettingsIconByName('goods-receipting'),
        destination: { kind: 'route', routeKey: 'settingsGoodsReceipting' },
      },
      {
        label: tCommon('sidebar.purchase-orders'),
        icon: getSettingsIconByName('purchase-orders'),
        destination: { kind: 'route', routeKey: 'settingsPurchaseOrders' },
      },
      {
        label: tCommon('sidebar.invoice-processing'),
        icon: getSettingsIconByName('invoice-processing'),
        destination: { kind: 'route', routeKey: 'settingsInvoiceProcessing' },
      },
    ],
  }

  const integrationsSection: SidebarSection = {
    title: tCommon('sidebar.integrations'),
    items: integrationsItems,
  }

  const sections: SidebarSection[] = [
    personalSettingSection,
    organisationSection,
    featuresSection,
    accountsPayableSection,
    integrationsSection,
  ]

  return <SidebarSections sections={sections} searchPlaceholder={tSettings('search-placeholder')} />
}
